/**
 * ReviewSubmitPage — Backlog B.2.x
 *
 * Standalone, deep-linkable review submission surface targeted by the
 * `review.request` notification email. URL: `/review/:bookingId`.
 *
 * AUTH-GATED — the URL itself carries NO tokens / secrets. The
 * `ProtectedRoute` wrapper enforces:
 *   • unauthed → /login?next=/review/:bookingId  (bounce back after login)
 *   • wrong role (provider / admin-only) → their own dashboard
 *
 * In-page guards (after the route gate passes, customer-authed):
 *   • booking not found / not the caller's → "Not your booking" panel
 *   • booking not yet completed → "Can't review yet" panel
 *   • booking already reviewed → "Already reviewed" panel w/ existing rating
 *
 * Submit path is the SAME endpoint as the in-dashboard ReviewModal:
 * POST /api/reviews  (single backend handler, single set of guards in
 * backend/reviews.py — no parallel write surface). This route only
 * adds a deep-link entrypoint; the backend math + idempotency is
 * untouched.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Star, ArrowLeft } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import StarRating from "@/components/reviews/StarRating";

const MAX_COMMENT = 500;

function PageShell({ children }) {
  return (
    <div
      className="min-h-screen bg-pp-bg px-4 py-10 sm:px-6 sm:py-16"
      data-testid="review-submit-page"
    >
      <div className="mx-auto w-full max-w-xl">
        <Link
          to="/dashboard/customer/bookings"
          className="mb-6 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-pp-text"
          data-testid="review-submit-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to My Plucks
        </Link>
        {children}
      </div>
    </div>
  );
}

function LoadingPanel() {
  return (
    <PageShell>
      <div
        className="flex items-center justify-center rounded-3xl border border-border bg-pp-surface px-8 py-16 text-muted-foreground"
        data-testid="review-submit-loading"
      >
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading your booking…
      </div>
    </PageShell>
  );
}

function ErrorPanel({ testId, title, body, ctaLabel, ctaHref }) {
  return (
    <PageShell>
      <div
        className="rounded-3xl border border-border bg-pp-surface p-8 shadow-sm"
        data-testid={testId}
      >
        <h1 className="mb-3 font-heading text-2xl font-bold text-pp-text">
          {title}
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">{body}</p>
        {ctaHref ? (
          <Button
            asChild
            className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
            data-testid={`${testId}-cta`}
          >
            <Link to={ctaHref}>{ctaLabel}</Link>
          </Button>
        ) : null}
      </div>
    </PageShell>
  );
}

export default function ReviewSubmitPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Page state machine.
  // status: "loading" | "ready" | "not_found" | "not_yours" |
  //         "not_completed" | "already_reviewed" | "submitted"
  const [status, setStatus] = useState("loading");
  const [booking, setBooking] = useState(null);
  const [existingReview, setExistingReview] = useState(null);

  // Form state.
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user) return; // ProtectedRoute will redirect; never reach here.
      setStatus("loading");
      try {
        // 1) Pull booking. The backend 404s for bookings the caller
        //    doesn't own (customer_id mismatch surfaces as 404 via the
        //    auth-aware loader in bookings.py:_load_booking_for).
        //
        //    Item E-follow-up — the single-booking endpoint now
        //    carries `has_review` + `last_review_rating` (mirrors the
        //    `/bookings/mine` enrichment). The legacy
        //    `/reviews/mine` fetch is gone; the rating render
        //    sources from the booking row itself.
        const { data: b } = await api.get(`/bookings/${bookingId}`);
        if (cancelled) return;
        // Guard — the caller should be the customer on this booking.
        if (b.customer_id !== user.id) {
          setStatus("not_yours");
          return;
        }
        if (b.status !== "completed") {
          setBooking(b);
          setStatus("not_completed");
          return;
        }
        // 2) Already-reviewed gate via the server-side `has_review`
        //    flag. Same source the dashboard banner + bookings page
        //    read; cross-surface drift is impossible.
        setBooking(b);
        if (b.has_review === true) {
          setExistingReview({
            rating: b.last_review_rating || null,
          });
          setStatus("already_reviewed");
          return;
        }
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        if (code === 404) {
          setStatus("not_found");
        } else if (code === 403) {
          setStatus("not_yours");
        } else {
          toast.error(
            formatApiErrorDetail(e?.response?.data?.detail) || e.message,
          );
          setStatus("not_found");
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [bookingId, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error("Please pick a rating before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const trimmed = comment.trim();
      const { data } = await api.post("/reviews", {
        booking_id: bookingId,
        rating,
        comment: trimmed.length > 0 ? trimmed : null,
      });
      toast.success("Thanks for your Pluck rating!");
      setExistingReview(data);
      setStatus("submitted");
    } catch (err) {
      const code = err?.response?.status;
      // 409 — already reviewed (race or replay). Flip to the
      // already-reviewed view rather than just toast-and-stay.
      if (code === 409) {
        setStatus("already_reviewed");
      }
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || err.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------- Render
  if (status === "loading") return <LoadingPanel />;

  if (status === "not_found") {
    return (
      <ErrorPanel
        testId="review-submit-not-found"
        title="Booking not found"
        body="We couldn't find this booking. The link may be expired or the booking has been cancelled."
        ctaLabel="Back to My Plucks"
        ctaHref="/dashboard/customer/bookings"
      />
    );
  }

  if (status === "not_yours") {
    return (
      <ErrorPanel
        testId="review-submit-not-yours"
        title="That booking isn't yours"
        body="This review link belongs to a different account. Log in as that customer to leave the review."
        ctaLabel="Back to My Plucks"
        ctaHref="/dashboard/customer/bookings"
      />
    );
  }

  if (status === "not_completed") {
    return (
      <ErrorPanel
        testId="review-submit-not-completed"
        title="Can't review this one yet"
        body="Your PooPartner hasn't completed this visit yet. You'll get a review prompt once it's done."
        ctaLabel="Back to My Plucks"
        ctaHref="/dashboard/customer/bookings"
      />
    );
  }

  if (status === "already_reviewed" || status === "submitted") {
    const r = existingReview || {};
    return (
      <PageShell>
        <div
          className="rounded-3xl border border-border bg-pp-surface p-8 shadow-sm"
          data-testid="review-submit-already-reviewed"
        >
          <h1 className="mb-3 font-heading text-2xl font-bold text-pp-text">
            {status === "submitted" ? "Thanks for your review!" : "Already reviewed"}
          </h1>
          <p className="mb-6 text-sm text-muted-foreground">
            {status === "submitted"
              ? "Your rating helps other dog owners find the right PooPartner."
              : "You've already left a review for this booking."}
          </p>
          {r.rating ? (
            <div
              className="mb-6 flex items-center gap-3"
              data-testid="review-submit-existing-rating"
            >
              <StarRating value={r.rating} readOnly size="md" />
              <span className="text-sm font-semibold text-pp-text">
                {r.rating} of 5
              </span>
            </div>
          ) : null}
          {r.comment ? (
            <blockquote
              className="mb-6 border-l-2 border-pp-orange pl-4 text-sm italic text-muted-foreground"
              data-testid="review-submit-existing-comment"
            >
              &ldquo;{r.comment}&rdquo;
            </blockquote>
          ) : null}
          <Button
            asChild
            className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
            data-testid="review-submit-already-cta"
          >
            <Link to="/dashboard/customer/bookings">Back to My Plucks</Link>
          </Button>
        </div>
      </PageShell>
    );
  }

  // status === "ready" — show the form.
  const b = booking || {};
  const providerName = b.provider_business_name || "your PooPartner";
  const shortId = (bookingId || "").slice(0, 8);

  return (
    <PageShell>
      <form
        onSubmit={handleSubmit}
        className="rounded-3xl border border-border bg-pp-surface p-8 shadow-sm"
        data-testid="review-submit-form"
      >
        <h1
          className="mb-1 font-heading text-2xl font-bold text-pp-text sm:text-3xl"
          data-testid="review-submit-title"
        >
          Rate My Pluck
        </h1>
        <p
          className="mb-6 text-sm text-muted-foreground"
          data-testid="review-submit-subtitle"
        >
          How did {providerName} do on your visit?
          {shortId ? (
            <span className="ml-1 text-xs uppercase tracking-wider">
              · Booking #{shortId}
            </span>
          ) : null}
        </p>

        <div className="mb-6 flex flex-col items-center gap-2">
          <StarRating
            value={rating}
            onChange={setRating}
            size="lg"
            testidPrefix="review-submit-star"
            ariaLabel="Pluck rating"
          />
          <span
            className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            data-testid="review-submit-rating-label"
          >
            {rating === 0 ? "Tap a star" : `${rating} of 5`}
          </span>
        </div>

        <div className="mb-6">
          <label
            htmlFor="review-submit-comment"
            className="mb-1 block text-sm font-semibold text-pp-text"
          >
            Comment{" "}
            <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <Textarea
            id="review-submit-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
            placeholder="Tell us how it went…"
            className="min-h-[120px] rounded-2xl"
            data-testid="review-submit-comment"
          />
          <p
            className="mt-1 text-right text-xs text-muted-foreground"
            data-testid="review-submit-comment-counter"
          >
            {comment.length}/{MAX_COMMENT}
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={submitting}
            onClick={() => navigate("/dashboard/customer/bookings")}
            className="rounded-full"
            data-testid="review-submit-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={rating < 1 || submitting}
            className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover disabled:opacity-60"
            data-testid="review-submit-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                <Star className="mr-2 h-4 w-4" />
                Submit review
              </>
            )}
          </Button>
        </div>
      </form>
    </PageShell>
  );
}
